// Confirm + run one bulk fix from the audit matrix; the backend re-checks every file's rule before touching it.
import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loader2, Wrench } from "lucide-react";
import { toast } from "sonner";
import { applyLegalAuditFix } from "@/lib/api";

const WHAT = {
  start_route:      "Starts the bankruptcy route at Eligibility check on each file — the gate G1 checklist, SLAs and route-entry actions run. Scotland / NI files and closed files are skipped.",
  align_stage_ld:   "Sets the stored stage back to Open loan so the account follows LoanDisk. The legal action, if any, stays on the file. A note is written on each file.",
  clear_hold:       "Clears the old legal_hold flag — the computed Journey already decides which lists a file is on. A note is written on each file.",
  close_fully_paid: "Closes the legal action as paid in full (LoanDisk shows Fully Paid): route → closed, ladder → fully paid, non-mandatory diary actions cancelled, note written on each file.",
};

export function AuditFixDialog({ req, onClose, onDone }) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  useEffect(() => { setResult(null); setBusy(false); }, [req]);
  if (!req) return null;
  const n = req.caseIds?.length || 0;
  const run = async () => {
    setBusy(true);
    try {
      const r = await applyLegalAuditFix(req.fix, req.caseIds);
      setResult(r);
      toast.success(`${req.label}: ${r.done.length} fixed${r.skipped.length ? `, ${r.skipped.length} skipped` : ""}${r.errors.length ? `, ${r.errors.length} failed` : ""}`);
    } catch (e) { toast.error("Fix failed: " + (e?.response?.data?.detail || e.message)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={!!req} onOpenChange={(o) => { if (!o && !busy) (result ? onDone : onClose)(); }}>
      <DialogContent className="sm:max-w-lg" data-testid="audit-fix-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Wrench className="w-4 h-4 text-rose-700" /> {req.label}</DialogTitle>
          <DialogDescription className="text-xs">{WHAT[req.fix] || req.rule}</DialogDescription>
        </DialogHeader>
        {!result ? (
          <div className="text-sm space-y-2">
            <p><span className="font-semibold tabular-nums" data-testid="audit-fix-count">{n}</span> file{n === 1 ? "" : "s"} currently fall under this rule. Each one is re-checked just before it is changed — anything that no longer matches is skipped.</p>
            <p className="text-[11px] text-muted-foreground border-l-2 border-slate-200 pl-2">{req.rule}</p>
          </div>
        ) : (
          <div className="text-sm space-y-1" data-testid="audit-fix-result">
            <div className="text-emerald-700 font-semibold">{result.done.length} fixed</div>
            {result.skipped.length > 0 && <div className="text-amber-700">{result.skipped.length} skipped — {result.skipped.slice(0, 3).map(s => s.why).join("; ")}{result.skipped.length > 3 ? "…" : ""}</div>}
            {result.errors.length > 0 && <div className="text-rose-700">{result.errors.length} failed — {result.errors.slice(0, 3).map(s => s.error).join("; ")}{result.errors.length > 3 ? "…" : ""}</div>}
          </div>
        )}
        <DialogFooter>
          {!result ? (
            <>
              <Button variant="outline" onClick={onClose} disabled={busy} data-testid="audit-fix-cancel">Cancel</Button>
              <Button className="bg-red-700 hover:bg-red-800 text-white" onClick={run} disabled={busy || n === 0} data-testid="audit-fix-confirm">
                {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Wrench className="w-4 h-4 mr-1.5" />} Fix {n} file{n === 1 ? "" : "s"}
              </Button>
            </>
          ) : <Button onClick={onDone} data-testid="audit-fix-done">Done</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
